import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear
} from '@components/timeline-new/utils'
import {TimelineNewStore} from './timeline-new-store'
import {CutoffData, VerticalStripeData} from './types'

export class MeasureHelper {
  constructor(private readonly store: TimelineNewStore) {}

  public calculateStripes(): void {
    this.store.state.weekendStripes = []
    this.store.state.intervalStripes = []

    if (['days', 'weeks'].includes(this.store.state.scale)) {
      const date = startOfWeek(new Date(this.store.state.leftBoundary))
      date.setDate(date.getDate() + 5)

      this.store.state.weekendStripes = this.prepareStripes(
        date,
        this.store.state.rightBoundary,
        () => 2 * Time.day,
        d => date.setDate(d.getDate() + 7)
      )
    }

    if (this.store.state.scale === 'days') {
      this.store.state.intervalStripes = this.prepareStripes(
        startOfDay(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        () => Time.day,
        d => d.setDate(d.getDate() + 1)
      )
    } else if (this.store.state.scale === 'weeks') {
      this.store.state.intervalStripes = this.prepareStripes(
        startOfWeek(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        () => Time.week,
        d => d.setDate(d.getDate() + 7)
      )
    } else if (['months', 'years'].includes(this.store.state.scale)) {
      this.store.state.intervalStripes = this.prepareStripes(
        startOfMonth(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        d0 => {
          const d1 = new Date(d0)
          d1.setMonth(d1.getMonth() + 1)
          return d1.getTime() - d0.getTime()
        },
        d => d.setMonth(d.getMonth() + 1)
      )
    }
  }

  private prepareStripes(
    date: Date,
    threshold: number,
    lengthFn: (date: Date) => number,
    nextFn: (date: Date) => number
  ): VerticalStripeData[] {
    const res: VerticalStripeData[] = []

    while (date.getTime() < threshold) {
      res.push({
        timestamp: date.getTime(),
        length: lengthFn(date)
      })
      date = new Date(nextFn(date))
    }

    return res
  }

  calculateCutoffs(): void {
    this.prepareCutoffsTop()
    this.prepareCutoffsBottom()
  }

  private prepareCutoffsTop(): void {
    if (
      this.store.state.scale === 'days' ||
      this.store.state.scale === 'weeks'
    ) {
      this.store.state.cutoffsTop = this.prepareCutoffs(
        startOfMonth(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        d => Lang.msg.monthNames[d.getMonth()] + ' ' + d.getFullYear(),
        d => d.setMonth(d.getMonth() + 1),
        true
      )
    } else if (
      this.store.state.scale === 'months' ||
      this.store.state.scale === 'years'
    ) {
      this.store.state.cutoffsTop = this.prepareCutoffs(
        startOfYear(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        d => d.getFullYear() + '',
        d => d.setFullYear(d.getFullYear() + 1),
        true
      )
    }
  }

  private prepareCutoffsBottom(): void {
    if (this.store.state.scale === 'days') {
      this.store.state.cutoffsBottom = this.prepareCutoffs(
        startOfDay(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        d => d.getDate() + '',
        d => d.setDate(d.getDate() + 1)
      )
    } else if (this.store.state.scale === 'weeks') {
      this.store.state.cutoffsBottom = this.prepareCutoffs(
        startOfWeek(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        d => d.getDate() + '',
        d => d.setDate(d.getDate() + 7)
      )
    } else if (
      this.store.state.scale === 'months' ||
      this.store.state.scale === 'years'
    ) {
      this.store.state.cutoffsBottom = this.prepareCutoffs(
        startOfMonth(new Date(this.store.state.leftBoundary)),
        this.store.state.rightBoundary,
        d => Lang.msg.monthsFirstForm[d.getMonth()],
        d => d.setMonth(d.getMonth() + 1)
      )
    }
  }

  private prepareCutoffs(
    date: Date,
    threshold: number,
    titleFn: (date: Date) => string,
    nextFn: (date: Date) => number,
    border?: boolean
  ): CutoffData[] {
    const res: CutoffData[] = []

    while (date.getTime() < threshold) {
      res.push({
        timestamp: date.getTime(),
        title: titleFn(date),
        border
      })
      date = new Date(nextFn(date))
    }

    return res
  }
}
